import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { VinCentrixLogo } from '../Logo/VinCentrixLogo';
import { 
  Bot, Calendar, BarChart3, Send, Mail, Phone, 
  Users, TrendingUp, CheckCircle, ArrowRight, ChevronLeft, 
  ChevronRight, Star, Quote, Zap, Shield, Clock, Award,
  HelpCircle, X, Menu, ChevronDown, Laptop, BarChart, 
  MessageSquare, FileText, BookOpen, Twitter, Linkedin, 
  Github, ArrowRight as ArrowRightIcon
} from 'lucide-react';

const Homepage = () => {
  const navigate = useNavigate();
  const [demoForm, setDemoForm] = useState({
    dealershipName: '',
    contactName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState({ products: false, company: false, resources: false });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${API_URL}/api/demo-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(demoForm)
      });

      if (!response.ok) {
        throw new Error('Failed to submit demo request');
      }

      const data = await response.json();
      alert('Thank you for your interest! We\'ll be in touch soon.');
      setDemoForm({ dealershipName: '', contactName: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting demo request:', error);
      alert('There was an error submitting your request. Please try again or contact us directly at support@vincentrix.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setDemoForm({
      ...demoForm,
      [e.target.name]: e.target.value
    });
  };

  const scrollToDemo = () => {
    const demoSection = document.getElementById('demo-section');
    demoSection?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #000000 0%, #0a0a10 100%)',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
    }}>
      {/* Sticky Navigation Header */}
      <header style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        background: scrolled 
          ? 'linear-gradient(145deg, rgba(255,255,255,0.1), rgba(255,255,255,0.02))' 
          : 'rgba(0, 0, 0, 0.3)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        padding: '16px 24px',
        zIndex: 1000,
        transition: 'all 0.3s ease'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          {/* Logo */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            cursor: 'pointer'
          }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <VinCentrixLogo size={44} showText={true} />
          </div>

          {/* Desktop Navigation */}
          <nav style={{
            display: 'flex',
            alignItems: 'center',
            gap: '32px'
          }}>
            <div 
              style={{ position: 'relative' }}
              onMouseEnter={() => setDropdownOpen({ ...dropdownOpen, products: true })}
              onMouseLeave={() => setDropdownOpen({ ...dropdownOpen, products: false })}
            >
              <button
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#fff',
                  fontSize: '15px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  transition: 'all 0.2s'
                }}
              >
                Products <ChevronDown size={16} />
              </button>
              {dropdownOpen.products && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: '0px',
                    background: 'linear-gradient(145deg, rgba(255,255,255,0.1), rgba(255,255,255,0.02))',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '16px',
                    padding: '8px',
                    minWidth: '220px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
                    zIndex: 1000
                  }}
                >
                  <a 
                    href="#features" 
                    style={{ 
                      display: 'block', 
                      padding: '12px 16px', 
                      color: 'rgba(255, 255, 255, 0.9)', 
                      textDecoration: 'none', 
                      borderRadius: '8px', 
                      transition: 'all 0.2s',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                      e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)';
                    }}
                  >
                    Features
                  </a>
                  <a 
                    href="#pricing" 
                    style={{ 
                      display: 'block', 
                      padding: '12px 16px', 
                      color: 'rgba(255, 255, 255, 0.9)', 
                      textDecoration: 'none', 
                      borderRadius: '8px', 
                      transition: 'all 0.2s',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                      e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)';
                    }}
                  >
                    Pricing
                  </a>
                  <a 
                    href="#demo-section" 
                    onClick={scrollToDemo}
                    style={{ 
                      display: 'block', 
                      padding: '12px 16px', 
                      color: 'rgba(255, 255, 255, 0.9)', 
                      textDecoration: 'none', 
                      borderRadius: '8px', 
                      transition: 'all 0.2s',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                      e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)';
                    }}
                  >
                    Request Demo
                  </a>
                </div>
              )}
            </div>
            <div 
              style={{ position: 'relative' }}
              onMouseEnter={() => setDropdownOpen({ ...dropdownOpen, company: true })}
              onMouseLeave={() => setDropdownOpen({ ...dropdownOpen, company: false })}
            >
              <button
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#fff',
                  fontSize: '15px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  transition: 'all 0.2s'
                }}
              >
                Company <ChevronDown size={16} />
              </button>
              {dropdownOpen.company && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: '0px',
                    background: 'linear-gradient(145deg, rgba(255,255,255,0.1), rgba(255,255,255,0.02))',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '16px',
                    padding: '8px',
                    minWidth: '220px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
                    zIndex: 1000
                  }}
                >
                  <a 
                    href="#about" 
                    style={{ 
                      display: 'block', 
                      padding: '12px 16px', 
                      color: 'rgba(255, 255, 255, 0.9)', 
                      textDecoration: 'none', 
                      borderRadius: '8px', 
                      transition: 'all 0.2s',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                      e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)';
                    }}
                  >
                    About Us
                  </a>
                  <a 
                    href="#careers" 
                    style={{ 
                      display: 'block', 
                      padding: '12px 16px', 
                      color: 'rgba(255, 255, 255, 0.9)', 
                      textDecoration: 'none', 
                      borderRadius: '8px', 
                      transition: 'all 0.2s',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                      e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)';
                    }}
                  >
                    Careers
                  </a>
                  <a 
                    href="#demo-section" 
                    style={{ 
                      display: 'block', 
                      padding: '12px 16px', 
                      color: 'rgba(255, 255, 255, 0.9)', 
                      textDecoration: 'none', 
                      borderRadius: '8px', 
                      transition: 'all 0.2s',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                      e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)';
                    }}
                  >
                    Contact
                  </a>
                </div>
              )}
            </div>
            <div 
              style={{ position: 'relative' }}
              onMouseEnter={() => setDropdownOpen({ ...dropdownOpen, resources: true })}
              onMouseLeave={() => setDropdownOpen({ ...dropdownOpen, resources: false })}
            >
              <button
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#fff',
                  fontSize: '15px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  transition: 'all 0.2s'
                }}
              >
                Resources <ChevronDown size={16} />
              </button>
              {dropdownOpen.resources && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: '0px',
                    background: 'linear-gradient(145deg, rgba(255,255,255,0.1), rgba(255,255,255,0.02))',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '16px',
                    padding: '8px',
                    minWidth: '220px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
                    zIndex: 1000
                  }}
                >
                  <a 
                    href="#blog" 
                    style={{ 
                      display: 'block', 
                      padding: '12px 16px', 
                      color: 'rgba(255, 255, 255, 0.9)', 
                      textDecoration: 'none', 
                      borderRadius: '8px', 
                      transition: 'all 0.2s',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                      e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)';
                    }}
                  >
                    Blog
                  </a>
                  <a 
                    href="#docs" 
                    style={{ 
                      display: 'block', 
                      padding: '12px 16px', 
                      color: 'rgba(255, 255, 255, 0.9)', 
                      textDecoration: 'none', 
                      borderRadius: '8px', 
                      transition: 'all 0.2s',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                      e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)';
                    }}
                  >
                    Documentation
                  </a>
                </div>
              )}
            </div>
            <button
              onClick={() => navigate('/login')}
              style={{
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                color: '#3b82f6',
                fontSize: '15px',
                fontWeight: '500',
                padding: '10px 20px',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.5)';
                e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
              }}
            >
              Login
            </button>
            <button
              onClick={scrollToDemo}
              style={{
                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                border: 'none',
                color: '#fff',
                fontSize: '15px',
                fontWeight: '600',
                padding: '10px 24px',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 0 20px rgba(59, 130, 246, 0.4)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 0 30px rgba(59, 130, 246, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.4)';
              }}
            >
              Request Demo
            </button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '120px 20px 80px',
        background: 'linear-gradient(135deg, #000000 0%, #0a0a10 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Gradient */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at 50% 0%, rgba(59, 130, 246, 0.15) 0%, transparent 70%)`,
          pointerEvents: 'none'
        }} />

        <div style={{
          maxWidth: '1200px',
          width: '100%',
          textAlign: 'center',
          position: 'relative',
          zIndex: 1
        }}>
          <h1 style={{
            fontSize: 'clamp(48px, 8vw, 80px)',
            fontWeight: '600',
            color: '#ffffff',
            marginBottom: '32px',
            lineHeight: '1.1',
            letterSpacing: '-2px',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
          }}>
            Turn Your Service Drive<br />Into Your #1 Sales Source
          </h1>
          
          <p style={{
            fontSize: 'clamp(18px, 2.5vw, 24px)',
            color: 'rgba(255, 255, 255, 0.6)',
            marginBottom: '48px',
            maxWidth: '800px',
            margin: '0 auto 48px',
            lineHeight: '1.6',
            fontWeight: '400',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
          }}>
            Stop losing service customers to competitors. VinCentrix AI reaches 100% of your service drive, converts 20%+, and delivers hot leads to your sales team.
          </p>

          <button
            onClick={scrollToDemo}
            style={{
              padding: '18px 48px',
              fontSize: '18px',
              fontWeight: '600',
              background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
              color: '#fff',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              boxShadow: '0 0 20px rgba(59, 130, 246, 0.4)',
              transition: 'all 0.3s ease',
              textTransform: 'none',
              fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 0 30px rgba(59, 130, 246, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.4)';
            }}
          >
            Request a Demo
          </button>

          {/* Hero Screenshot */}
          <div style={{
            marginTop: '80px',
            maxWidth: '1200px',
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <img
              src="/images/dashboard.png"
              alt="VinCentrix Dashboard"
              style={{
                width: '100%',
                maxWidth: '1200px',
                height: 'auto',
                borderRadius: '12px',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.8)',
                border: 'none'
              }}
            />
          </div>
        </div>
      </section>

      {/* SMS Opt-In CTA Banner */}
      <div style={{
        background: 'linear-gradient(145deg, rgba(255,255,255,0.1), rgba(255,255,255,0.02))',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '20px',
        padding: '60px 40px',
        margin: '40px auto',
        maxWidth: '1200px',
        color: 'white',
        textAlign: 'center',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
      }}>
        <h2 style={{ 
          fontSize: '42px', 
          marginBottom: '20px', 
          fontWeight: '800',
          lineHeight: '1.2'
        }}>
          📱 Get Exclusive Vehicle Offers via SMS
        </h2>
        <p style={{ 
          fontSize: '20px', 
          marginBottom: '32px', 
          opacity: 0.95,
          maxWidth: '700px',
          margin: '0 auto 32px'
        }}>
          Subscribe to receive instant notifications about great trade-in offers and vehicle deals sent directly to your phone
        </p>
        <a 
          href="/sms-opt-in"
          style={{
            background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
            color: '#fff',
            padding: '18px 48px',
            borderRadius: '12px',
            fontSize: '18px',
            fontWeight: '600',
            textDecoration: 'none',
            display: 'inline-block',
            transition: 'all 0.3s ease',
            boxShadow: '0 0 20px rgba(59, 130, 246, 0.4)',
            cursor: 'pointer'
          }}
          onMouseOver={(e) => {
            e.target.style.transform = 'translateY(-1px)';
            e.target.style.boxShadow = '0 0 30px rgba(59, 130, 246, 0.6)';
          }}
          onMouseOut={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.4)';
          }}
        >
          Subscribe Now →
        </a>
        <p style={{ 
          marginTop: '20px', 
          fontSize: '14px', 
          opacity: 0.85 
        }}>
          No commitment • Cancel anytime • Free to join
        </p>
      </div>

      {/* Why Choose Us Section */}
      <section style={{
        padding: '120px 20px',
        background: 'linear-gradient(135deg, #000000 0%, #0a0a10 100%)',
        position: 'relative'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontSize: 'clamp(36px, 5vw, 56px)',
            fontWeight: '600',
            color: '#ffffff',
            marginBottom: '80px',
            textAlign: 'center',
            letterSpacing: '-1px',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
          }}>
            What Better Service Drive<br />Acquisition Looks Like
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '48px',
            marginTop: '60px'
          }}>
            {/* Feature 1 */}
            <div style={{
              padding: '40px',
              background: 'linear-gradient(145deg, rgba(255,255,255,0.1), rgba(255,255,255,0.02))',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '20px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '24px',
                boxShadow: '0 0 20px rgba(59, 130, 246, 0.4)'
              }}>
                <Clock size={32} color="#fff" />
              </div>
              <h3 style={{
                fontSize: '24px',
                fontWeight: '600',
                color: '#ffffff',
                marginBottom: '16px',
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
              }}>
                Never Miss a Customer
              </h3>
              <p style={{
                fontSize: '16px',
                color: 'rgba(255, 255, 255, 0.6)',
                lineHeight: '1.6',
                margin: 0,
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
              }}>
                AI responds within minutes, 24/7. Every service customer gets contacted automatically, ensuring no opportunity slips through the cracks.
              </p>
            </div>

            {/* Feature 2 */}
            <div style={{
              padding: '40px',
              background: 'linear-gradient(145deg, rgba(255,255,255,0.1), rgba(255,255,255,0.02))',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '20px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '24px',
                boxShadow: '0 0 20px rgba(59, 130, 246, 0.4)'
              }}>
                <TrendingUp size={32} color="#fff" />
              </div>
              <h3 style={{
                fontSize: '24px',
                fontWeight: '600',
                color: '#ffffff',
                marginBottom: '16px',
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
              }}>
                10x Your Conversions
              </h3>
              <p style={{
                fontSize: '16px',
                color: 'rgba(255, 255, 255, 0.6)',
                lineHeight: '1.6',
                margin: 0,
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
              }}>
                From 2% to 20%+ capture rate. Industry-leading conversion rates that turn your service drive into a revenue engine.
              </p>
            </div>

            {/* Feature 3 */}
            <div style={{
              padding: '40px',
              background: 'linear-gradient(145deg, rgba(255,255,255,0.1), rgba(255,255,255,0.02))',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '20px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '24px',
                boxShadow: '0 0 20px rgba(59, 130, 246, 0.4)'
              }}>
                <Zap size={32} color="#fff" />
              </div>
              <h3 style={{
                fontSize: '24px',
                fontWeight: '600',
                color: '#ffffff',
                marginBottom: '16px',
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
              }}>
                Automated Pipeline
              </h3>
              <p style={{
                fontSize: '16px',
                color: 'rgba(255, 255, 255, 0.6)',
                lineHeight: '1.6',
                margin: 0,
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
              }}>
                From text to appointment to sale. Complete automation that handles the entire customer journey from first contact to acquisition.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section with Screenshots */}
      <section id="features" style={{
        padding: '120px 20px',
        background: '#000000',
        position: 'relative'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          {/* Feature 1: AI-Powered Engagement - Text LEFT, Image RIGHT */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '80px',
            alignItems: 'center',
            marginBottom: '120px'
          }}>
            <div>
              <h2 style={{
                fontSize: 'clamp(36px, 5vw, 48px)',
                fontWeight: '600',
                color: '#fff',
                marginBottom: '24px',
                letterSpacing: '-1px',
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
              }}>
                AI-Powered Engagement
              </h2>
              <p style={{
                fontSize: '18px',
                color: 'rgba(255, 255, 255, 0.6)',
                lineHeight: '1.7',
                marginBottom: '32px',
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
              }}>
                Sydney bot automatically reaches out to every service customer with personalized trade-in offers. Natural conversations, intelligent responses, and seamless handoffs to your team when needed.
              </p>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}>
                {['100% service customer coverage', 'Personalized offers using VIN & service history', 'Natural conversation handling', 'Automatic escalation to sales team'].map((item) => (
                  <li key={item} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontSize: '16px',
                    color: 'rgba(255, 255, 255, 0.9)'
                  }}>
                    <CheckCircle size={20} color="#3b82f6" style={{ flexShrink: 0 }} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <img
                src="/images/simulator.png"
                alt="AI-Powered Engagement"
                style={{
                  width: '100%',
                  maxWidth: '600px',
                  height: 'auto',
                  borderRadius: '12px',
                  display: 'block',
                  border: 'none'
                }}
              />
            </div>
          </div>

          {/* Feature 2: Real-Time Analytics - Image LEFT, Text RIGHT */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '80px',
            alignItems: 'center',
            marginBottom: '120px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <img
                src="/images/analytics.png"
                alt="Real-Time Analytics"
                style={{
                  width: '100%',
                  maxWidth: '600px',
                  height: 'auto',
                  borderRadius: '12px',
                  display: 'block',
                  border: 'none'
                }}
              />
            </div>
            <div>
              <h2 style={{
                fontSize: 'clamp(36px, 5vw, 48px)',
                fontWeight: '600',
                color: '#fff',
                marginBottom: '24px',
                letterSpacing: '-1px',
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
              }}>
                Real-Time Analytics
              </h2>
              <p style={{
                fontSize: '18px',
                color: 'rgba(255, 255, 255, 0.6)',
                lineHeight: '1.7',
                marginBottom: '32px',
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
              }}>
                Track acquisition rates, conversions, and team performance with detailed insights. Make data-driven decisions to optimize your acquisition process.
              </p>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}>
                {['Live acquisition metrics', 'Conversion tracking', 'Team performance insights', 'Revenue analytics'].map((item) => (
                  <li key={item} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontSize: '16px',
                    color: 'rgba(255, 255, 255, 0.9)'
                  }}>
                    <CheckCircle size={20} color="#3b82f6" style={{ flexShrink: 0 }} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Feature 3: Compliant Messaging - Text LEFT, Image RIGHT */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '80px',
            alignItems: 'center',
            marginBottom: '120px'
          }}>
            <div>
              <h2 style={{
                fontSize: 'clamp(36px, 5vw, 48px)',
                fontWeight: '600',
                color: '#fff',
                marginBottom: '24px',
                letterSpacing: '-1px',
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
              }}>
                Compliant Messaging
              </h2>
              <p style={{
                fontSize: '18px',
                color: 'rgba(255, 255, 255, 0.6)',
                lineHeight: '1.7',
                marginBottom: '32px',
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
              }}>
                Full TCPA compliance built-in. Automatic opt-out handling, consent management, and compliance monitoring to keep you protected.
              </p>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}>
                {['TCPA compliant', 'Automatic opt-out handling', 'Consent management', 'Compliance monitoring'].map((item) => (
                  <li key={item} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontSize: '16px',
                    color: 'rgba(255, 255, 255, 0.9)'
                  }}>
                    <CheckCircle size={20} color="#3b82f6" style={{ flexShrink: 0 }} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <img
                src="/images/customers.png"
                alt="Compliant Messaging"
                style={{
                  width: '100%',
                  maxWidth: '600px',
                  height: 'auto',
                  borderRadius: '12px',
                  display: 'block',
                  border: 'none'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" style={{
        padding: '120px 20px',
        background: 'linear-gradient(135deg, #000000 0%, #0a0a10 100%)',
        position: 'relative'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <h2 style={{
            fontSize: 'clamp(36px, 5vw, 56px)',
            fontWeight: '600',
            color: '#ffffff',
            marginBottom: '20px',
            letterSpacing: '-1px',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
          }}>
            Choose Your Plan
          </h2>
          <p style={{
            fontSize: '18px',
            color: 'rgba(255, 255, 255, 0.6)',
            marginBottom: '60px',
            maxWidth: '600px',
            margin: '0 auto 60px',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
          }}>
            Custom pricing tailored to your dealership's needs. Contact us for a personalized quote.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '32px',
            marginTop: '60px'
          }}>
            {/* Starter Plan */}
            <div style={{
              background: 'linear-gradient(145deg, rgba(255,255,255,0.1), rgba(255,255,255,0.02))',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.1)',
              padding: '40px',
              borderRadius: '20px',
              position: 'relative',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-8px)';
              e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2), 0 0 30px rgba(59, 130, 246, 0.3)';
              e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
            }}
            >
              <h3 style={{
                fontSize: '24px',
                fontWeight: '600',
                color: '#ffffff',
                marginBottom: '12px',
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
              }}>
                Starter
              </h3>
              <p style={{
                fontSize: '14px',
                color: 'rgba(255, 255, 255, 0.6)',
                marginBottom: '32px',
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
              }}>
                Perfect for small dealerships
              </p>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: '0 0 32px 0',
                textAlign: 'left',
                flex: 1
              }}>
                {[
                  'Up to 500 customers',
                  '1 user account',
                  'AI engagement',
                  'Basic analytics',
                  'Email support'
                ].map((feature) => (
                  <li key={feature} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '16px',
                    fontSize: '16px',
                    color: '#333333'
                  }}>
                    <CheckCircle size={20} color="#3b82f6" style={{ flexShrink: 0 }} />
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                onClick={scrollToDemo}
                style={{
                  width: '100%',
                  padding: '14px',
                  fontSize: '16px',
                  fontWeight: '600',
                  background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginTop: 'auto',
                  boxShadow: '0 0 20px rgba(59, 130, 246, 0.4)',
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 0 30px rgba(59, 130, 246, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.4)';
                }}
              >
                Contact for Pricing
              </button>
            </div>

            {/* Professional Plan */}
            <div style={{
              background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
              padding: '40px',
              borderRadius: '20px',
              border: 'none',
              position: 'relative',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 0 30px rgba(59, 130, 246, 0.5)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-8px)';
              e.currentTarget.style.boxShadow = '0 0 40px rgba(59, 130, 246, 0.7)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 0 30px rgba(59, 130, 246, 0.5)';
            }}
            >
              {/* Most Popular Badge */}
              <div style={{
                position: 'absolute',
                top: '-12px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: '#fff',
                color: '#3b82f6',
                padding: '6px 20px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '700',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
              }}>
                Most Popular
              </div>
              <h3 style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#fff',
                marginBottom: '12px',
                marginTop: '8px'
              }}>
                Professional
              </h3>
              <p style={{
                fontSize: '14px',
                color: 'rgba(255, 255, 255, 0.9)',
                marginBottom: '32px'
              }}>
                Ideal for growing dealerships
              </p>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: '0 0 32px 0',
                textAlign: 'left',
                flex: 1
              }}>
                {[
                  'Up to 2,000 customers',
                  '5 user accounts',
                  'AI engagement',
                  'Advanced analytics',
                  'Priority support',
                  'Custom branding'
                ].map((feature) => (
                  <li key={feature} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '16px',
                    fontSize: '16px',
                    color: 'rgba(255, 255, 255, 0.95)',
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
                  }}>
                    <CheckCircle size={20} color="#fff" style={{ flexShrink: 0 }} />
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                onClick={scrollToDemo}
                style={{
                  width: '100%',
                  padding: '14px',
                  fontSize: '16px',
                  fontWeight: '600',
                  background: '#fff',
                  color: '#3b82f6',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginTop: 'auto',
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                Contact for Pricing
              </button>
            </div>

            {/* Enterprise Plan */}
            <div style={{
              background: 'linear-gradient(145deg, rgba(255,255,255,0.1), rgba(255,255,255,0.02))',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.1)',
              padding: '40px',
              borderRadius: '20px',
              position: 'relative',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-8px)';
              e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2), 0 0 30px rgba(59, 130, 246, 0.3)';
              e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
            }}
            >
              <h3 style={{
                fontSize: '24px',
                fontWeight: '600',
                color: '#ffffff',
                marginBottom: '12px',
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
              }}>
                Enterprise
              </h3>
              <p style={{
                fontSize: '14px',
                color: 'rgba(255, 255, 255, 0.6)',
                marginBottom: '32px',
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
              }}>
                For high-volume dealerships
              </p>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: '0 0 32px 0',
                textAlign: 'left',
                flex: 1
              }}>
                {[
                  'Unlimited customers',
                  'Unlimited users',
                  'AI engagement',
                  'Custom analytics',
                  'Dedicated support',
                  'API access',
                  'White-label option'
                ].map((feature) => (
                  <li key={feature} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '16px',
                    fontSize: '16px',
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
                  }}>
                    <CheckCircle size={20} color="#3b82f6" style={{ flexShrink: 0 }} />
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                onClick={scrollToDemo}
                style={{
                  width: '100%',
                  padding: '14px',
                  fontSize: '16px',
                  fontWeight: '600',
                  background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginTop: 'auto',
                  boxShadow: '0 0 20px rgba(59, 130, 246, 0.4)',
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 0 30px rgba(59, 130, 246, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.4)';
                }}
              >
                Contact Sales
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section style={{
        padding: '120px 20px',
        background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <h2 style={{
            fontSize: 'clamp(36px, 5vw, 56px)',
            fontWeight: '600',
            color: '#fff',
            marginBottom: '60px',
            letterSpacing: '-1px',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
          }}>
            The Numbers Don't Lie
          </h2>

          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '80px',
            flexWrap: 'wrap',
            marginBottom: '60px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: 'clamp(48px, 8vw, 72px)',
                fontWeight: '900',
                color: '#fff',
                marginBottom: '12px',
                lineHeight: '1'
              }}>
                500
              </div>
              <div style={{
                fontSize: '18px',
                color: 'rgba(255, 255, 255, 0.9)',
                fontWeight: '500',
                marginBottom: '8px'
              }}>
                Service Customers
              </div>
              <div style={{
                fontSize: '24px',
                color: '#fff',
                fontWeight: '700'
              }}>
                →
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: 'clamp(48px, 8vw, 72px)',
                fontWeight: '900',
                color: '#fff',
                marginBottom: '12px',
                lineHeight: '1'
              }}>
                100
              </div>
              <div style={{
                fontSize: '18px',
                color: 'rgba(255, 255, 255, 0.9)',
                fontWeight: '500',
                marginBottom: '8px'
              }}>
                Acquisition Deals
              </div>
              <div style={{
                fontSize: '24px',
                color: '#fff',
                fontWeight: '700'
              }}>
                →
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: 'clamp(48px, 8vw, 72px)',
                fontWeight: '900',
                color: '#fff',
                marginBottom: '12px',
                lineHeight: '1'
              }}>
                $250K
              </div>
              <div style={{
                fontSize: '18px',
                color: 'rgba(255, 255, 255, 0.9)',
                fontWeight: '500'
              }}>
                Additional Revenue
              </div>
            </div>
          </div>

          <p style={{
            fontSize: '20px',
            color: 'rgba(255, 255, 255, 0.95)',
            fontWeight: '500',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            Average dealership results per month with VinCentrix
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section id="demo-section" style={{
        padding: '120px 20px',
        background: '#000000',
        position: 'relative'
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <h2 style={{
            fontSize: 'clamp(36px, 5vw, 56px)',
            fontWeight: '800',
            color: '#fff',
            marginBottom: '24px',
            letterSpacing: '-1px'
          }}>
            Ready to Transform Your Service Drive?
          </h2>
          <p style={{
            fontSize: '20px',
            color: 'rgba(255, 255, 255, 0.7)',
            marginBottom: '48px',
            lineHeight: '1.6'
          }}>
            Join 500+ dealerships capturing more service customers and increasing acquisition revenue.
          </p>

          <form onSubmit={handleDemoSubmit} style={{
            background: 'linear-gradient(145deg, rgba(255,255,255,0.1), rgba(255,255,255,0.02))',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.1)',
            padding: '48px',
            borderRadius: '20px',
            textAlign: 'left',
            maxWidth: '600px',
            margin: '0 auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
          }}>
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                color: '#fff',
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '8px'
              }}>
                Dealership Name *
              </label>
              <input
                type="text"
                name="dealershipName"
                required
                value={demoForm.dealershipName}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  background: '#0a0a0a',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  fontSize: '15px',
                  color: '#fff',
                  outline: 'none',
                  transition: 'all 0.3s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'rgba(59, 130, 246, 0.5)';
                  e.target.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                color: '#fff',
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '8px'
              }}>
                Contact Name *
              </label>
              <input
                type="text"
                name="contactName"
                required
                value={demoForm.contactName}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  background: '#0a0a0a',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  fontSize: '15px',
                  color: '#fff',
                  outline: 'none',
                  transition: 'all 0.3s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'rgba(59, 130, 246, 0.5)';
                  e.target.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                color: '#fff',
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '8px'
              }}>
                Email *
              </label>
              <input
                type="email"
                name="email"
                required
                value={demoForm.email}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  background: '#0a0a0a',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  fontSize: '15px',
                  color: '#fff',
                  outline: 'none',
                  transition: 'all 0.3s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'rgba(59, 130, 246, 0.5)';
                  e.target.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                }}
              />
            </div>

            <div style={{ marginBottom: '32px' }}>
              <label style={{
                display: 'block',
                color: '#fff',
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '8px'
              }}>
                Phone *
              </label>
              <input
                type="tel"
                name="phone"
                required
                value={demoForm.phone}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  background: '#0a0a0a',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  fontSize: '15px',
                  color: '#fff',
                  outline: 'none',
                  transition: 'all 0.3s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'rgba(59, 130, 246, 0.5)';
                  e.target.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                }}
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                width: '100%',
                padding: '16px',
                fontSize: '18px',
                fontWeight: '700',
                background: isSubmitting ? '#666' : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                color: '#fff',
                border: 'none',
                borderRadius: '12px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
                onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 0 30px rgba(59, 130, 246, 0.6)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.4)';
              }}
            >
              {isSubmitting ? (
                <>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '3px solid rgba(255,255,255,0.3)',
                    borderTopColor: '#fff',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite'
                  }} />
                  Submitting...
                </>
              ) : (
                <>
                  Request Demo
                  <ArrowRightIcon size={20} />
                </>
              )}
            </button>
          </form>
        </div>
      </section>


      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        html {
          scroll-behavior: smooth;
        }
        
        @media (max-width: 1024px) {
          section > div > div[style*="grid-template-columns: 1fr 1fr"] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Homepage;
